export interface TemplateColorScheme {
  name: string;
  primary: string;
  secondary: string;
  text: string;
  background: string;
  accent?: string;
}

export interface TemplateFontSettings {
  heading: string;
  body: string;
  size: {
    heading: number;
    subheading: number;
    body: number;
  };
  lineHeight?: {
    heading?: number;
    body?: number;
  };
}

export interface TemplateDefinition {
  name: string;
  description: string;
  category: string;
  layout_type: 'single_column' | 'two_column' | 'sidebar' | 'split';
  template_data: {
    sections: string[];
    sectionOrder: string[];
    headerStyle: string;
    spacing: {
      section: number;
      item: number;
      line: number;
    };
    alignment: string;
    bulletStyle: string;
  };
  color_schemes: TemplateColorScheme[];
  font_settings: TemplateFontSettings;
  features: string[];
  ats_score: number;
  popularity_score: number;
  industry_tags: string[];
  is_premium: boolean;
  is_active: boolean;
}

export const professionalTemplates: TemplateDefinition[] = [
  {
    name: 'Classic Professional',
    description: 'Traditional single-column layout perfect for any industry. Clean, ATS-friendly, and timeless design.',
    category: 'professional',
    layout_type: 'single_column',
    template_data: {
      sections: ['header', 'summary', 'experience', 'education', 'skills', 'certifications'],
      sectionOrder: ['header', 'summary', 'experience', 'education', 'skills', 'certifications'],
      headerStyle: 'centered',
      spacing: {
        section: 20,
        item: 12,
        line: 1.6,
      },
      alignment: 'left',
      bulletStyle: 'disc',
    },
    color_schemes: [
      {
        name: 'Professional Blue',
        primary: '#1e3a8a',
        secondary: '#64748b',
        text: '#1f2937',
        background: '#ffffff',
        accent: '#3b82f6',
      },
    ],
    font_settings: {
      heading: 'Georgia, serif',
      body: 'Arial, sans-serif',
      size: {
        heading: 28,
        subheading: 16,
        body: 11,
      },
      lineHeight: {
        heading: 1.2,
        body: 1.6,
      },
    },
    features: ['ATS Optimized', 'Single Column', 'Clean Layout', 'Traditional'],
    ats_score: 98,
    popularity_score: 150,
    industry_tags: ['Business', 'Finance', 'Healthcare', 'Education', 'General'],
    is_premium: false,
    is_active: true,
  },
  {
    name: 'Modern Two-Column',
    description: 'Contemporary design with sidebar for contact info and skills. Perfect for showcasing technical expertise.',
    category: 'modern',
    layout_type: 'two_column',
    template_data: {
      sections: ['header', 'summary', 'experience', 'education', 'skills', 'certifications'],
      sectionOrder: ['header', 'summary', 'experience', 'education', 'skills', 'certifications'],
      headerStyle: 'left-aligned',
      spacing: {
        section: 18,
        item: 10,
        line: 1.5,
      },
      alignment: 'left',
      bulletStyle: 'square',
    },
    color_schemes: [
      {
        name: 'Modern Slate',
        primary: '#0f172a',
        secondary: '#475569',
        text: '#334155',
        background: '#ffffff',
        accent: '#0ea5e9',
      },
    ],
    font_settings: {
      heading: 'Helvetica, Arial, sans-serif',
      body: 'Helvetica, Arial, sans-serif',
      size: {
        heading: 26,
        subheading: 14,
        body: 10.5,
      },
      lineHeight: {
        heading: 1.3,
        body: 1.5,
      },
    },
    features: ['Two Column', 'Modern Design', 'Sidebar Layout', 'ATS Compatible'],
    ats_score: 94,
    popularity_score: 180,
    industry_tags: ['Technology', 'Marketing', 'Design', 'Sales'],
    is_premium: false,
    is_active: true,
  },
  {
    name: 'Executive',
    description: 'Bold and authoritative design for senior-level positions. Emphasizes leadership and achievements.',
    category: 'executive',
    layout_type: 'single_column',
    template_data: {
      sections: ['header', 'summary', 'experience', 'education', 'skills', 'certifications'],
      sectionOrder: ['header', 'summary', 'experience', 'skills', 'education', 'certifications'],
      headerStyle: 'bold-top',
      spacing: {
        section: 22,
        item: 14,
        line: 1.7,
      },
      alignment: 'left',
      bulletStyle: 'circle',
    },
    color_schemes: [
      {
        name: 'Executive Navy',
        primary: '#1e40af',
        secondary: '#6366f1',
        text: '#111827',
        background: '#ffffff',
        accent: '#8b5cf6',
      },
    ],
    font_settings: {
      heading: 'Times New Roman, serif',
      body: 'Calibri, sans-serif',
      size: {
        heading: 30,
        subheading: 17,
        body: 11,
      },
      lineHeight: {
        heading: 1.2,
        body: 1.7,
      },
    },
    features: ['Executive Level', 'Achievement Focused', 'Bold Headers', 'Premium Look'],
    ats_score: 96,
    popularity_score: 120,
    industry_tags: ['Executive', 'Management', 'Consulting', 'Finance', 'Legal'],
    is_premium: false,
    is_active: true,
  },
  {
    name: 'Technical Pro',
    description: 'Optimized for software engineers, developers, and IT professionals. Highlights technical skills prominently.',
    category: 'technical',
    layout_type: 'two_column',
    template_data: {
      sections: ['header', 'summary', 'skills', 'experience', 'education', 'certifications'],
      sectionOrder: ['header', 'skills', 'summary', 'experience', 'education', 'certifications'],
      headerStyle: 'compact',
      spacing: {
        section: 16,
        item: 10,
        line: 1.5,
      },
      alignment: 'left',
      bulletStyle: 'dash',
    },
    color_schemes: [
      {
        name: 'Tech Green',
        primary: '#047857',
        secondary: '#059669',
        text: '#374151',
        background: '#ffffff',
        accent: '#10b981',
      },
    ],
    font_settings: {
      heading: 'Consolas, Monaco, monospace',
      body: 'Roboto, Arial, sans-serif',
      size: {
        heading: 24,
        subheading: 14,
        body: 10.5,
      },
      lineHeight: {
        heading: 1.3,
        body: 1.5,
      },
    },
    features: ['Tech Optimized', 'Skills First', 'Compact Design', 'GitHub Friendly'],
    ats_score: 95,
    popularity_score: 200,
    industry_tags: ['Technology', 'Software Engineering', 'Data Science', 'IT', 'Cybersecurity'],
    is_premium: false,
    is_active: true,
  },
  {
    name: 'Creative Professional',
    description: 'Subtle creative touches while maintaining professionalism. Perfect balance of style and ATS compatibility.',
    category: 'creative',
    layout_type: 'sidebar',
    template_data: {
      sections: ['header', 'summary', 'experience', 'skills', 'education', 'certifications'],
      sectionOrder: ['header', 'summary', 'experience', 'skills', 'education', 'certifications'],
      headerStyle: 'accent-bar',
      spacing: {
        section: 18,
        item: 11,
        line: 1.6,
      },
      alignment: 'left',
      bulletStyle: 'custom',
    },
    color_schemes: [
      {
        name: 'Creative Orange',
        primary: '#ea580c',
        secondary: '#f97316',
        text: '#292524',
        background: '#ffffff',
        accent: '#fb923c',
      },
    ],
    font_settings: {
      heading: 'Montserrat, sans-serif',
      body: 'Open Sans, sans-serif',
      size: {
        heading: 26,
        subheading: 15,
        body: 10.5,
      },
      lineHeight: {
        heading: 1.3,
        body: 1.6,
      },
    },
    features: ['Creative Design', 'Color Accents', 'Sidebar Layout', 'Modern Style'],
    ats_score: 92,
    popularity_score: 160,
    industry_tags: ['Marketing', 'Design', 'Media', 'Advertising', 'Creative'],
    is_premium: false,
    is_active: true,
  },
];
